import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { LayoutDashboard, Gamepad2, MapPin, CalendarClock, CalendarDays, Ticket, Trophy, Megaphone, Swords, FileBarChart, LogOut, Truck, Loader2, Eye, FileText, Shield } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const nav = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, feature: null },
  { to: "/admin/content", label: "Content", icon: FileText, feature: "content" },
  { to: "/admin/games", label: "Games", icon: Gamepad2, feature: "games" },
  { to: "/admin/locations", label: "Locations", icon: MapPin, feature: "locations" },
  { to: "/admin/slots", label: "Slots", icon: CalendarClock, feature: "slots" },
  { to: "/admin/holidays", label: "Holidays", icon: CalendarDays, feature: "holidays" },
  { to: "/admin/bookings", label: "Bookings", icon: Ticket, anyOf: ["bookings_view", "bookings_edit"] },
  { to: "/admin/leaderboard", label: "Leaderboard", icon: Trophy, feature: "leaderboard" },
  { to: "/admin/marketing", label: "Marketing", icon: Megaphone, feature: "marketing" },
  { to: "/admin/challenges", label: "Challenges", icon: Swords, feature: "challenges" },
  { to: "/admin/reports", label: "Reports", icon: FileBarChart, feature: "reports" },
  { to: "/admin/admins", label: "Admins", icon: Shield, feature: "admins", superOnly: true },
];

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => { if (!loading && !user) navigate("/admin/login"); }, [user, loading]);

  if (loading || user === null) return <div className="min-h-screen grid place-items-center bg-slate-950 text-slate-400"><Loader2 className="animate-spin" /></div>;
  if (!user) return null;

  const superAdmin = !!user.is_super;
  const perms = user.permissions || [];
  const canSee = (n) => !n.feature && !n.anyOf ? true : superAdmin ? true : n.superOnly ? false : n.anyOf ? n.anyOf.some((f) => perms.includes(f)) : perms.includes(n.feature);
  const visibleNav = nav.filter(canSee);
  const current = [...nav].reverse().find((n) => (n.end ? pathname === n.to : pathname.startsWith(n.to)));
  const denied = current && !canSee(current);

  return (
    <div className="min-h-screen flex bg-slate-950">
      <aside className="w-60 shrink-0 border-r border-white/10 bg-[#0d1320] hidden md:flex flex-col">
        <div className="h-16 flex items-center gap-2 px-5 border-b border-white/10">
          <div className="h-8 w-8 rounded-lg bg-violet-600 grid place-items-center"><Truck className="h-4 w-4 text-white" /></div>
          <span className="font-display font-extrabold">Admin</span>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {visibleNav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`admin-nav-${n.label.toLowerCase()}`}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive ? "bg-violet-500/15 text-violet-300" : "text-slate-400 hover:text-white hover:bg-white/5"}`}>
              <n.icon className="h-4 w-4" /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10">
          <div className="text-xs text-slate-500 px-3 mb-2 truncate">{user.email}</div>
          <a href="/" target="_blank" rel="noreferrer" data-testid="admin-view-site"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-400 hover:text-white hover:bg-white/5 w-full">
            <Eye className="h-4 w-4" /> View site as user
          </a>
          <button onClick={() => { logout(); navigate("/admin/login"); }} data-testid="admin-logout"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-400 hover:text-white hover:bg-white/5 w-full">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>
      <main className="flex-1 overflow-x-hidden">
        <div className="md:hidden border-b border-white/10">
          <div className="h-14 flex items-center px-4 gap-2">
            <Truck className="h-5 w-5 text-violet-400" /> <span className="font-display font-bold">Admin</span>
            <div className="ml-auto flex items-center gap-1">
              <a href="/" target="_blank" rel="noreferrer" data-testid="admin-view-site-mobile" aria-label="View site as user"
                className="p-2.5 min-h-11 min-w-11 grid place-items-center rounded-lg text-slate-400 hover:text-white hover:bg-white/5" title="View site as user"><Eye className="h-5 w-5" /></a>
              <button onClick={() => { logout(); navigate("/admin/login"); }} data-testid="admin-logout-mobile" aria-label="Sign out"
                className="p-2.5 min-h-11 min-w-11 grid place-items-center rounded-lg text-slate-400 hover:text-white hover:bg-white/5" title="Sign out"><LogOut className="h-5 w-5" /></button>
            </div>
          </div>
          <div className="flex gap-2 overflow-x-auto text-xs px-4 pb-2">
            {visibleNav.map((n) => <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => `px-2 py-1 rounded whitespace-nowrap ${isActive ? "bg-violet-500/20 text-violet-300" : "text-slate-400"}`}>{n.label}</NavLink>)}
          </div>
        </div>
        <div className="p-6">
          {denied ? (
            <div className="max-w-md mx-auto mt-20 text-center" data-testid="access-denied">
              <Shield className="h-10 w-10 text-slate-600 mx-auto mb-4" />
              <h2 className="font-display font-bold text-xl mb-1">No access to this section</h2>
              <p className="text-slate-400 text-sm">Your account doesn't have permission here. Contact the super admin if you need access.</p>
            </div>
          ) : <Outlet />}
        </div>
      </main>
    </div>
  );
}
